import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, MapPin, Music2, Calendar, Banknote, Check, X, Trash2, Megaphone, ChevronRight } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const STATUS_PILL = {
  open: "bg-sage-100 text-sage-700",
  filled: "bg-ivory-100 text-ink-600",
  closed: "bg-ivory-100 text-ink-500",
};

const APP_PILL = {
  applied: "bg-amber-soft text-amber-deep",
  confirmed: "bg-sage-100 text-sage-700",
  declined: "bg-terracotta-50 text-terracotta-700",
  withdrawn: "bg-ivory-100 text-ink-500",
};

export default function GigDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [g, setG] = useState(null);
  const [applyForSeat, setApplyForSeat] = useState(null);
  const [applyMsg, setApplyMsg] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/gigs/${id}`);
      setG(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!g) return <div className="text-ink-500">Loading…</div>;

  const apply = async (seatId, message) => {
    setBusy(true);
    try {
      await api.post(`/gigs/${id}/apply`, { seat_id: seatId, message });
      toast.success("Application sent");
      setApplyForSeat(null);
      setApplyMsg("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const confirm = async (appId) => {
    try {
      await api.post(`/gigs/${id}/applications/${appId}/confirm`);
      toast.success("Confirmed — booking created and added to their diary");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const decline = async (appId) => {
    try {
      await api.post(`/gigs/${id}/applications/${appId}/decline`);
      toast.success("Declined");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const withdraw = async (appId) => {
    if (!window.confirm("Withdraw your application?")) return;
    try {
      await api.delete(`/gigs/${id}/applications/${appId}`);
      toast.success("Application withdrawn");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const closeGig = async () => {
    if (!window.confirm("Close this gig? No more applications can be sent.")) return;
    await api.post(`/gigs/${id}/close`);
    load();
  };

  const removeGig = async () => {
    if (!window.confirm("Delete this gig and all applications?")) return;
    await api.delete(`/gigs/${id}`);
    navigate("/gigs");
  };

  const isMusician = user?.role === "musician";
  const isOwner = g.is_owner;
  const appsBySeat = {};
  for (const a of g.applications || []) {
    (appsBySeat[a.seat_id] = appsBySeat[a.seat_id] || []).push(a);
  }
  const myAppForSeat = (seatId) => (g.applications || []).find(
    (a) => a.musician_id === user.id && a.seat_id === seatId && a.status !== "declined" && a.status !== "withdrawn"
  );

  return (
    <div className="space-y-5 max-w-3xl" data-testid="gig-detail-page">
      <Link to="/gigs" className="btn-ghost text-sm w-fit"><ArrowLeft size={16} /> Back to gigs</Link>

      <div className="card-base p-6 sm:p-8">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="label-overline inline-flex items-center gap-2"><Megaphone size={12} /> Gig</div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight mt-1">{g.title}</h1>
            <div className="text-sm text-ink-600 mt-1">Posted by {g.booker_name}</div>
          </div>
          <span className={`pill capitalize ${STATUS_PILL[g.status]}`} data-testid="gig-status">{g.status}</span>
        </div>

        <div className="grid sm:grid-cols-2 gap-3 mt-5 text-sm">
          <Info icon={Calendar} label="When">
            {new Date(g.start_at).toLocaleString(undefined, { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
            {" – "}
            {new Date(g.end_at).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}
          </Info>
          {(g.venue || g.location) && <Info icon={MapPin} label="Where">{g.venue || g.location}</Info>}
          {g.ensemble_name && <Info icon={Music2} label="Ensemble">{g.ensemble_name}</Info>}
          {g.fee_info && <Info icon={Banknote} label="Fee">{g.fee_info}</Info>}
        </div>

        {g.repertoire && <div className="mt-4 p-3 rounded-xl bg-ivory-100/60 text-sm"><span className="label-overline">Repertoire</span><div className="text-ink-800 mt-1">{g.repertoire}</div></div>}
        {g.description && <div className="mt-3 p-3 rounded-xl bg-ivory-100/60 text-sm"><span className="label-overline">Notes</span><div className="text-ink-800 mt-1 whitespace-pre-line">{g.description}</div></div>}

        {isOwner && g.status === "open" && (
          <div className="mt-5 flex flex-wrap gap-2">
            <button onClick={closeGig} className="btn-secondary text-sm" data-testid="gig-close-btn">Close gig</button>
            <button onClick={removeGig} className="btn-ghost text-sm text-terracotta-600" data-testid="gig-delete-btn"><Trash2 size={14} /> Delete</button>
          </div>
        )}
      </div>

      <section className="space-y-3">
        <h2 className="font-heading text-xl font-semibold">Seats</h2>
        {(g.seats || []).map((s) => {
          const open = (s.count_needed || 0) - (s.confirmed || 0);
          const full = open <= 0;
          const apps = appsBySeat[s.id] || [];
          const myApp = myAppForSeat(s.id);
          return (
            <div key={s.id} className="card-base p-5" data-testid={`seat-card-${s.id}`}>
              <div className="flex items-start justify-between gap-3 mb-2">
                <div>
                  <div className="font-heading text-lg font-semibold">{s.instrument} <span className="text-ink-500 text-sm font-normal">× {s.count_needed}</span></div>
                  {s.notes && <div className="text-sm text-ink-600 mt-0.5">{s.notes}</div>}
                </div>
                <span className={`pill ${full ? "bg-ivory-100 text-ink-500" : "bg-sage-100 text-sage-700"}`}>
                  {full ? "Filled" : `${open} open`}
                </span>
              </div>

              {isMusician && g.status === "open" && !full && !myApp && (
                applyForSeat === s.id ? (
                  <div className="mt-3 space-y-2">
                    <textarea rows={2} className="input-field" placeholder="Quick note for the booker (optional)" value={applyMsg} onChange={(e) => setApplyMsg(e.target.value)} data-testid={`apply-msg-${s.id}`} />
                    <div className="flex gap-2">
                      <button onClick={() => apply(s.id, applyMsg)} disabled={busy} className="btn-primary text-sm" data-testid={`apply-confirm-${s.id}`}>Send application</button>
                      <button onClick={() => { setApplyForSeat(null); setApplyMsg(""); }} className="btn-secondary text-sm">Cancel</button>
                    </div>
                  </div>
                ) : (
                  <button onClick={() => setApplyForSeat(s.id)} className="btn-primary text-sm mt-3" data-testid={`apply-btn-${s.id}`}>I'm available</button>
                )
              )}

              {myApp && (
                <div className="mt-3 flex items-center gap-2 p-3 rounded-xl bg-sage-100/60">
                  <Check size={14} className="text-sage-600" />
                  <div className="text-sm">You've applied — <span className="capitalize">{myApp.status}</span></div>
                  {myApp.status === "applied" && (
                    <button onClick={() => withdraw(myApp.id)} className="btn-ghost text-xs ml-auto text-terracotta-600" data-testid={`withdraw-${myApp.id}`}>Withdraw</button>
                  )}
                </div>
              )}

              {isOwner && (
                <div className="mt-4 border-t border-ivory-200 pt-3 space-y-2">
                  <div className="label-overline">{apps.length} application{apps.length === 1 ? "" : "s"}</div>
                  {apps.length === 0 && <div className="text-sm text-ink-500">No applications yet.</div>}
                  {apps.map((a) => (
                    <div key={a.id} className="flex items-start justify-between gap-3 p-3 rounded-xl bg-ivory-100/60" data-testid={`app-${a.id}`}>
                      <Link to={`/musicians/${a.musician_id}`} className="flex-1 min-w-0">
                        <div className="font-medium inline-flex items-center gap-1.5">
                          {a.musician_name}
                          <ChevronRight size={14} className="text-ink-400" />
                        </div>
                        <div className="text-xs text-ink-500">{a.musician_instrument || a.instrument}{a.musician_location ? ` · ${a.musician_location}` : ""}{a.musician_level ? ` · ${a.musician_level.replace("_", " ")}` : ""}</div>
                        {a.message && <div className="text-sm text-ink-700 mt-1 italic">"{a.message}"</div>}
                      </Link>
                      <div className="flex flex-col gap-1 items-end shrink-0">
                        <span className={`pill ${APP_PILL[a.status]} capitalize`}>{a.status}</span>
                        {a.status === "applied" && (
                          <div className="flex gap-1">
                            <button onClick={() => confirm(a.id)} className="btn-primary text-xs" data-testid={`confirm-${a.id}`}><Check size={12} /> Confirm</button>
                            <button onClick={() => decline(a.id)} className="btn-ghost text-xs text-terracotta-600" data-testid={`decline-${a.id}`}><X size={12} /></button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </section>
    </div>
  );
}

function Info({ icon: Icon, label, children }) {
  return (
    <div className="flex items-start gap-3 p-3 rounded-xl bg-ivory-100/60">
      <div className="w-8 h-8 grid place-items-center rounded-lg bg-white text-sage-600 border border-ivory-200"><Icon size={16} /></div>
      <div>
        <div className="label-overline text-ink-500">{label}</div>
        <div className="text-sm text-ink-800 mt-0.5">{children}</div>
      </div>
    </div>
  );
}
